import type { StepFile } from "../../../dtos/StepFile";

// A step's uploaded files, split by whether one of its configured documents claims them. A file is
// claimed when its stored name (minus the extension) is a document's saved-as name - the same match
// the document rows use, so it holds however the file was typed.
//
// Orphans are what a document removed from the template leaves behind: still in SharePoint, but no
// longer part of what the subtask is asking for. They are called out on the subtask and left out of
// its file count.
type FileLike = { name: string };
type FileSettings = { unrestrictedUpload?: boolean; fileRestrictions?: StepFile[] | null; fileLimit?: number | null } | null | undefined;
type CommentFiles = { files?: FileLike[] | null } | null | undefined;
type StepLike = {
    files?: FileLike[] | null;
    requestComment?: CommentFiles;
    responseComment?: CommentFiles;
    revisionRequestComments?: CommentFiles[] | null;
};

const stripExtension = (name: string) => name.replace(/\.[^/.]+$/, "");

export const configuredDocuments = (settings: FileSettings): StepFile[] | null =>
    (!settings?.unrestrictedUpload ? settings?.fileRestrictions : null) ?? null;

export const splitConfiguredFiles = <T extends FileLike>(
    files: T[],
    settings: FileSettings
): { claimed: T[]; orphaned: T[] } => {
    const documents = configuredDocuments(settings);

    // Nothing is configured, so nothing can be orphaned - every file belongs to the step.
    if (!documents?.length) return { claimed: files, orphaned: [] };

    const claimedNames = documents.map(d => d.rename).filter((r): r is string => !!r);
    const isClaimed = (file: T) => claimedNames.includes(stripExtension(file.name));

    return {
        claimed: files.filter(isClaimed),
        orphaned: files.filter(f => !isClaimed(f)),
    };
};

// Cached to ensure referential stability across component re-renders (e.g. window resize).
// DevExtreme FileUploader invalidates and clears DOM if allowedFileExtensions reference changes.
const extensionCache = new Map<string, string[]>();

export const uploadExtensions = (configured: string[] | null | undefined): string[] | null => {
    if (!configured?.length) return null;
    const key = configured.map(e => e.replace(/^\./, "").toLowerCase()).join(",");
    const cached = extensionCache.get(key);
    if (cached) return cached;
    const result = configured.map(e => `.${e.replace(/^\./, "").toLowerCase()}`);
    extensionCache.set(key, result);
    return result;
};

// Every file on a subtask, whichever area it was uploaded to. This is the count the server checks
// an upload against, so it is the count the uploader has to match.
export const countStepFiles = (step: StepLike): number =>
    (step.files?.length ?? 0)
    + (step.requestComment?.files?.length ?? 0)
    + (step.responseComment?.files?.length ?? 0)
    + (step.revisionRequestComments ?? []).reduce((n, c) => n + (c?.files?.length ?? 0), 0);

// The most files a subtask takes, or null when there is no ceiling. A subtask with configured
// documents takes one file per document; one that takes anything honors its own limit, if set.
export const stepFileLimit = (settings: FileSettings): number | null => {
    const documents = configuredDocuments(settings);
    if (documents?.length) return documents.length;
    const limit = settings?.fileLimit ?? null;
    return limit != null && limit > 0 ? limit : null;
};
